/**
 * The sidebar — every menu item this session may open, projected through the
 * effective permission set and **folded under seven headings** above 900px.
 *
 * See `../nav/menu.ts` for the projection itself and for the reason this is not a
 * security boundary. This file only renders what those functions return.
 *
 * `DESIGN.md` §10: "the sidebar's menu items are a list, not a grid of divs" — so
 * `<nav><h?><ul><li><a>`, which is what lets a screen reader say "list, 7 items" and
 * lets a keyboard user tab through them in document order.
 *
 * ── Why it folds (UI-13, `DESIGN.md` §12 *Luật Rail Gọn*) ────────────────────────
 *
 * Forty-two items is about 1 900px of column. *Luật Cạnh Bàn Đứng Yên* forbids the
 * desk edge scrolling itself back, so on a 900px window the item you were standing
 * on — `/presence`, item forty of forty-two — carried its `aria-current` marker
 * below the fold and nobody could see it. Folding is the way out that keeps both
 * rules: one heading open at a time is seven headings (36px) plus at most nine items
 * (44px) plus the footer, which fits with room to spare.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';

import { useT } from '../i18n';
import {
  canOpen,
  menuGroupForPath,
  UI_STATES_ITEM,
  visibleMenuGroups,
  visibleMenuItems,
  type MenuGroupId,
  type MenuItem,
} from '../nav/menu';
import type { DashboardPermission } from '../api/types';
import { EmptyState } from '../states/EmptyState';
import { Icon } from '../states/icons';
import { Skeleton } from '../states/Skeleton';

/**
 * The one width at which this rail is a horizontally scrolling strip — the same
 * `900px` breakpoint `styles/shell.css` lies the rail down at, written here so the
 * effect below and the stylesheet cannot drift apart silently.
 */
export const RAIL_STRIP_QUERY = '(max-width: 900px)';

/**
 * Where a viewer's own folding is remembered. Per browser, not per server: which
 * headings somebody keeps shut is a seating preference, not a fact about the
 * company, and it has no business in an API.
 */
export const RAIL_GROUPS_KEY = 'wp.rail.groups';

type GroupState = Partial<Record<MenuGroupId, boolean>>;

function readGroupState(): GroupState {
  // Private mode, a blocked origin and a half-written value are all the same answer:
  // no preference yet. A rail must never fail to render because storage said no.
  try {
    const raw = window.localStorage.getItem(RAIL_GROUPS_KEY);
    const parsed: unknown = raw === null ? null : JSON.parse(raw);
    return parsed !== null && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeGroupState(state: GroupState): void {
  try {
    window.localStorage.setItem(RAIL_GROUPS_KEY, JSON.stringify(state));
  } catch {
    // Nothing to do and nothing to say: the fold still works for this session.
  }
}

/**
 * Is this the ≤900px strip?
 *
 * Read synchronously in the initialiser rather than in an effect, so a phone does
 * not paint the folded column for one frame before flattening it. jsdom implements
 * no `matchMedia`, which is the desktop answer — and the shape every existing shell
 * test was written against.
 */
function useIsStrip(): boolean {
  const [strip, setStrip] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia(RAIL_STRIP_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(RAIL_STRIP_QUERY);
    const sync = (): void => {
      setStrip(query.matches);
    };
    sync();
    query.addEventListener('change', sync);
    return () => {
      query.removeEventListener('change', sync);
    };
  }, []);
  return strip;
}

export interface SidebarProps {
  /** `SessionUser.permissions` — the effective set. `null` while `/auth/me` is in flight. */
  readonly permissions: readonly DashboardPermission[] | null;
}

export function Sidebar({ permissions }: SidebarProps): ReactNode {
  const t = useT();
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const isStrip = useIsStrip();
  const [folds, setFolds] = useState<GroupState>(readGroupState);

  const toggle = useCallback((id: MenuGroupId, open: boolean) => {
    setFolds((previous) => {
      const next = { ...previous, [id]: open };
      writeGroupState(next);
      return next;
    });
  }, []);

  // Below 900px the rail lies down as a horizontally scrolling strip, and the item
  // you have just navigated to can be three screens along it. Bring it back into
  // view on every route change.
  //
  // `inline: 'center'`, not `'nearest'`: the strip's right edge carries a 32px fade
  // (`shell.css`), and `'nearest'` stops the moment the element is *technically*
  // inside the scrollport — which on `/people` left "Nhân sự" sitting under the
  // gradient with two of its three syllables painted out. The browser cannot see a
  // mask, so the scroll has to be the one that does not stop at the edge. Measured
  // on the 390px capture, both ways.
  //
  // ── And ONLY below 900px (fix round 2, R1) ──────────────────────────────────
  //
  // Above 900px the column is its own vertical scrollport, so scrolling it by code
  // moves the desk edge's contents out from under the eye and stops it being the
  // fixed landmark **Luật Cạnh Bàn Đứng Yên** says it is. Since UI-13 there is also
  // nothing left to scroll to: the folded column shows the current heading's items
  // inside the first viewport, which is what **Luật Rail Gọn** buys.
  //
  // Guarded twice: jsdom implements neither `matchMedia` nor layout, and
  // `scrollIntoView` frequently is stubbed away too. A missing method is not a
  // reason to crash the rail.
  useEffect(() => {
    if (!isStrip) return;
    const current = navRef.current?.querySelector('[aria-current="page"]');
    if (current instanceof HTMLElement && typeof current.scrollIntoView === 'function') {
      current.scrollIntoView({ inline: 'center', block: 'nearest' });
    }
    // `permissions` is in the deps as well as `pathname`: on a cold load the rail is a
    // skeleton until `/auth/me` answers, so the first run finds no `aria-current` at
    // all. Re-running when the menu itself arrives is what makes the scroll land on
    // the first page a person opens, not only on the second.
  }, [isStrip, pathname, permissions]);

  // In flight: bars, no links. A guessed menu is worse than an empty one — see
  // `../auth/SessionProvider.tsx`.
  if (permissions === null) {
    return (
      <nav className="wp-shell__nav" aria-label={t('shell.mainNav')} ref={navRef}>
        <Skeleton rows={4} label={t('shell.loadingSession')} testId="sidebar-skeleton" />
      </nav>
    );
  }

  const renderItem = (item: MenuItem): ReactNode => (
    <li key={item.id}>
      <NavLink className="wp-nav__link" to={item.path} data-testid={`nav-${item.id}`}>
        {/* 16px, 1.8 stroke, muted at rest — the world's rail spec. It is
            `aria-hidden` beside its own word, so nothing is announced twice and
            nothing is carried by the glyph alone. */}
        <Icon name={item.icon} className="wp-nav__icon" />
        {t(item.labelKey)}
      </NavLink>
    </li>
  );

  const flat = visibleMenuItems(permissions);
  const groups = visibleMenuGroups(permissions);
  // Forced open, and not merely defaulted open: a viewer who shut this heading
  // yesterday still has to see where they are standing today.
  //
  // A route outside the menu (`/ui-states`, a not-found) matches no heading, and so
  // does a heading this session holds nothing in — a path typed by hand into a module
  // they cannot open. Either way the answer is the first heading they *can* see, not
  // a hard-coded `daily`: a viewer whose rail starts at "Tuyển dụng" used to get all
  // seven shut, which is the rail *Luật Rail Gọn* exists to prevent.
  const matched = menuGroupForPath(pathname);
  const standingIn = groups.some((group) => group.id === matched) ? matched : groups[0]?.id;

  return (
    <nav className="wp-shell__nav" aria-label={t('shell.mainNav')} ref={navRef}>
      {flat.length === 0 ? (
        // Reachable: one `deny workforce.view_self` row leaves an ordinary employee
        // with an empty effective set and `scope_level: null`, a case
        // `src/modules/auth/service.ts` documents and keeps answering rather than
        // erroring. A blank rail would look like a broken build; this says what
        // happened and who fixes it.
        <EmptyState
          title={t('nav.empty.title')}
          description={t('nav.empty.body')}
          testId="sidebar-empty"
        />
      ) : isStrip ? (
        // The strip is unchanged by UI-13: one flat row, no headings. A 56px band
        // has no second line to put a heading on, and it already solves the
        // find-your-place problem by scrolling the current item to the middle.
        <ul className="wp-nav__list" data-testid="sidebar-menu">
          {flat.map(renderItem)}
        </ul>
      ) : (
        <div className="wp-nav__groups" data-testid="sidebar-menu">
          {groups.map((group) => {
            // The heading you are standing under is held open, so it is not a toggle
            // at all: `aria-disabled` rather than `disabled`, which keeps it in the
            // tab order for a screen reader to read the hint off. Clicking used to
            // write `false` to storage while `aria-expanded` stayed `true` — a control
            // that looks broken *and* quietly rewrites a preference. Leaving the group
            // (a route change) hands the viewer's remembered fold back.
            const standing = group.id === standingIn;
            const open = standing || (folds[group.id] ?? false);
            return (
              <div className="wp-nav__group-block" key={group.id}>
                <button
                  type="button"
                  className="wp-nav__group"
                  aria-expanded={open}
                  aria-disabled={standing || undefined}
                  data-testid={`nav-group-${group.id}`}
                  onClick={
                    standing
                      ? undefined
                      : () => {
                          toggle(group.id, !open);
                        }
                  }
                >
                  {/* Chevron down when open, pointing at the list it reveals; a
                      quarter-turn when shut. `aria-hidden` — `aria-expanded` is
                      what a screen reader reads, the glyph is for the eye. */}
                  <Icon name="chevron-down" className="wp-nav__caret" />
                  {t(group.labelKey)}
                  {standing ? (
                    <span className="wp-visually-hidden"> {t('nav.group.standingHint')}</span>
                  ) : null}
                </button>
                {/*
                 * `hidden` rather than an unrendered branch, so find-in-page and a
                 * screen reader's browse mode still reach a folded item's word the
                 * way `.wp-disclosure`'s `<details>` does (DESIGN.md §14). Hidden
                 * items are out of the tab order, which is the half that matters
                 * for a keyboard.
                 */}
                <ul className="wp-nav__list" hidden={!open}>
                  {group.items.map(renderItem)}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Not one of the menu items and deliberately not in the lists above: a
          developer/reviewer reference that renders fixture values only. See
          `web/shapes/ui-states.md`. Offered only to the sessions the route lets in
          (UI-12) — a link that leads to a refusal is worse than no link. It sits in
          the footer rather than under a heading, so no fold can hide it. */}
      {canOpen(UI_STATES_ITEM, permissions) ? (
        <div className="wp-nav__footer">
          <NavLink className="wp-nav__link" to={UI_STATES_ITEM.path} data-testid="nav-ui-states">
            <Icon name={UI_STATES_ITEM.icon} className="wp-nav__icon" />
            {t(UI_STATES_ITEM.labelKey)}
          </NavLink>
        </div>
      ) : null}
    </nav>
  );
}
